import PropTypes from 'prop-types'

export const labelComponent = PropTypes.oneOfType([
  PropTypes.func,
  PropTypes.elementType,
])

export const fieldOptions = PropTypes.arrayOf(
  PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
    PropTypes.bool,
    PropTypes.shape({
      key: PropTypes.string.isRequired,
      value: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.number,
        PropTypes.bool,
      ]).isRequired,
    }),
  ])
)

//  MODEL TYPES
//  ---------

export const location = PropTypes.shape({
  id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
  name: PropTypes.string,
  addressLineOne: PropTypes.string,
  addressLineTwo: PropTypes.string,
})

export const product = PropTypes.shape({
  id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
  name: PropTypes.string,
})

export const machineAsset = PropTypes.shape({
  id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
  make: PropTypes.string,
  serialNumber: PropTypes.string,
  name: PropTypes.string,
})

export const image = PropTypes.shape({
  id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
  url: PropTypes.string,
  filename: PropTypes.string,
})

export const TRIP_QUESTION_SECTION = {
  CHECK_IN_STANDARD: 'check_in_standard',
  CHECK_IN_CUSTOM: 'check_in_custom',
  CHECK_OUT_STANDARD: 'check_out_standard',
  CHECK_OUT_CUSTOM: 'check_out_custom',
}

export const TripQuestionResponse = PropTypes.shape({
  questionText: PropTypes.string,
  type: PropTypes.string,
  questionResponse: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.arrayOf(image),
  ]),
  section: PropTypes.oneOf(Object.values(TRIP_QUESTION_SECTION)),
})

export const trip = PropTypes.shape({
  id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
  workOrderId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  date: PropTypes.string,
  createdAt: PropTypes.string,
  checkedOutAt: PropTypes.string,
  notes: PropTypes.string,
  abuseNeglectClaims: PropTypes.string,
  rootCause: PropTypes.string,
  status: PropTypes.string,
  currentTripImages: PropTypes.arrayOf(image),
  operable: PropTypes.string,
  questionnaireCompleted: PropTypes.bool,
  checkinGeofenceStatus: PropTypes.string,
  checkoutGeofenceStatus: PropTypes.string,
  allQuestionResponses: PropTypes.arrayOf(TripQuestionResponse),
})

export const Question = PropTypes.shape({
  // Constraints are defined by ValidateJS. See: https://validatejs.org/
  constraints: PropTypes.object,
  constraintText: PropTypes.string,
  fieldName: PropTypes.string,
  options: PropTypes.array,
  photoType: PropTypes.string,
  placeholder: PropTypes.string,
  questionId: PropTypes.string,
  required: PropTypes.bool,
  title: PropTypes.string,
  type: PropTypes.string,
})

export const Questionnaire = PropTypes.shape({
  type: PropTypes.string.isRequired,
  questions: PropTypes.arrayOf(Question),
  step: PropTypes.string.isRequired,
})

// A single entry in the check-out form's ordered `fields` list. Most entries are data
// fields (choice / input / textarea / photo) rendered from the API description. A few are
// "slot" markers (type: 'slot') that tell the client where to inject its own root-cause
// widgets, whose UI, options, and branching validation stay frontend-owned.
export const CheckoutField = PropTypes.shape({
  type: PropTypes.string.isRequired,
  // Slot markers only carry `type: 'slot'` and the slot name.
  slot: PropTypes.string,
  // Data fields
  constraints: PropTypes.object,
  constraintText: PropTypes.string,
  fieldName: PropTypes.string,
  options: PropTypes.array,
  photoType: PropTypes.string,
  placeholder: PropTypes.string,
  questionId: PropTypes.string,
  required: PropTypes.bool,
  title: PropTypes.string,
})

// Describes the check-out form as a single ordered list of fields. The backend decides
// the fields, their order, and which root-cause slots (if any) apply to this work order.
export const CheckOutForm = PropTypes.shape({
  fields: PropTypes.arrayOf(CheckoutField),
})

export const workOrder = PropTypes.shape({
  id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
  name: PropTypes.string,
  companyName: PropTypes.string,
  problemDescription: PropTypes.string,
  workPerformed: PropTypes.string,
  orderStatus: PropTypes.string,
  orderSubStatus: PropTypes.string,
  nte: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  scheduledServiceDueDate: PropTypes.string,
  slaDate: PropTypes.string,
  etaDate: PropTypes.string,
  acceptedDateTime: PropTypes.string,
  etaTime: PropTypes.string,
  techComments: PropTypes.string,
  partnerTransactionLog: PropTypes.string,
  workCompleteDateTime: PropTypes.string,
  rootCause: PropTypes.string,
  abuseNeglectClaims: PropTypes.string,
  ivrPhoneNumber: PropTypes.string,
  ivrPinPartnerCode: PropTypes.string,
  ivrOrderNumber: PropTypes.string,
  ivrInstructions: PropTypes.string,
  partsOrderPoNumber: PropTypes.string,
  auditDate: PropTypes.string,
  reviewReason: PropTypes.string,
  auditNotes: PropTypes.string,
  acceptDecline: PropTypes.string,
  recalled: PropTypes.string,
  installation: PropTypes.bool,
  requiresQuestionnaireCompletion: PropTypes.bool,
  shouldRunGeolocationCheck: PropTypes.bool,
  trips: PropTypes.arrayOf(trip),
  machineAsset: machineAsset,
  location: location,
  questionnaire: Questionnaire,
  checkOutForm: CheckOutForm,
  geolocationCheckinMostRecentStatus: PropTypes.string,
})

export const user = PropTypes.shape({
  id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
  email: PropTypes.string,
  name: PropTypes.string,
})

export const selectOption = PropTypes.shape({
  label: PropTypes.string.isRequired,
  value: PropTypes.string.isRequired,
})

export const WorkOrderIndexEntryConfig = PropTypes.shape({
  key: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  textColor: PropTypes.string,
  fallback: PropTypes.string,
  sortable: PropTypes.bool,
  link: PropTypes.bool,
  isDateTime: PropTypes.bool,
})

export const WorkOrderIndexEntry = PropTypes.shape({
  id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
  name: PropTypes.string.isRequired,
  location: PropTypes.string,
  city: PropTypes.string,
  mostRecentAssignedDateTime: PropTypes.string,
  agingAsp: PropTypes.number,
  agingRni: PropTypes.number,
  workCompleteDateTime: PropTypes.string,
  etaDate: PropTypes.string,
  orderStatusGrouped: PropTypes.string,
  orderType: PropTypes.string,
})

export const ManagerDashboardFilterValues = PropTypes.shape({
  list: PropTypes.string,
  location: PropTypes.string,
  city: PropTypes.string,
  orderType: PropTypes.string,
})

//  FORM TYPES
//  ---------

export const input = PropTypes.shape({
  value: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.array,
    PropTypes.number,
    PropTypes.bool,
  ]),
  name: PropTypes.string.isRequired,
  onBlur: PropTypes.func,
  onChange: PropTypes.func,
})

export const meta = PropTypes.shape({
  dirty: PropTypes.bool,
  error: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.arrayOf(PropTypes.string),
  ]),
  invalid: PropTypes.bool,
  pristine: PropTypes.bool,
  touched: PropTypes.bool,
  valid: PropTypes.bool,
})

export const field = PropTypes.shape({
  name: PropTypes.string,
  onBlur: PropTypes.func,
  onChange: PropTypes.func,
  value: PropTypes.any,
})

//  UTIL TYPES
//  ---------

export const route = PropTypes.shape({
  url: PropTypes.string.isRequired,
  component: PropTypes.oneOfType([PropTypes.object, PropTypes.func]).isRequired,
  layoutParams: PropTypes.object,
  provider: PropTypes.object,
})

//  CONSTANTS
//  ---------

export const TOAST_DEFAULT_DURATION = 3000 // ms
export const WORK_STATUS_CHARACTER_LIMIT = 750 // characters
export const ABUSE_NEGLECT_NOTES_CHARACTER_LIMIT = 255
export const OPERATIONAL_OPTIONS = ['yes', 'no']

export const ERROR_CODE = {
  TRIP_TIMED_OUT: 'trip_timed_out',
  NOT_FOUND: 'not_found',
  NOT_FOR_TSS: 'not_for_tss_app',
  INVALID_ORDER_STATUS_CHANGE: 'invalid_order_status_change',
  WORK_ORDER_ACTION_NOT_ALLOWED: 'work_order_action_not_allowed',
  UNKNOWN_ERROR: 'unknown_error',
}

export const ERROR_MESSAGES = {
  GEO_DENIAL_ERROR:
    'This app requires geolocation tracking. There was a geolocating error submitting',
}

export const ORDER_STATUS = {
  ASSIGNED: 'Assigned',
  AUDIT: 'Audit',
  AWAITING_PARTS: 'Awaiting Parts',
  CANCELED: 'Canceled',
  CLOSED: 'Closed',
  ESTIMATE: 'Estimate',
  GOFER_SALES: 'Gofer Sales',
  ON_HOLD: 'On Hold',
  PENDING_ASSIGNMENT: 'Pending Assignment',
  PENDING_REASSIGNMENT: 'Pending Reassignment',
  SCHEDULED: 'Scheduled',
  TECH_ON_SITE: 'Tech On Site',
  TRIAGE: 'Triage',
  WORK_COMPLETED: 'Work Completed',
}

export const ORDER_SUB_STATUS = {
  ESTIMATE_APPROVED: 'Estimate Approved',
  IVR_TIMEOUT: 'IVR Timeout',
  PENDING_ESTIMATE_APPROVAL: 'Pending Estimate Approval',
}

export const PHOTO_TYPE = {
  CUSTOM: 'CustomTripPhoto',
  MACHINE_STATE: 'PreWorkMachineStateTripPhoto',
  SERIAL_NUMBER: 'SerialNumberTripPhoto',
  UNIT_CONDITION: 'UnitConditionTripPhoto',
  WORK_PERFORMED: 'WorkPerformedTripPhoto',
  ABUSE: 'AbuseTripPhoto',
  DEFECT: 'DefectTripPhoto',
}

export const QUESTION_TYPE = {
  CHOICE: 'choice',
  INPUT: 'input',
  TEXTAREA: 'textarea',
  PHOTO: 'photo',
}

// The check-out form's `fields` list can contain "slot" markers alongside real data
// fields. A slot tells the client to render one of its own root-cause widgets at that
// position; the backend never describes the root-cause UI itself.
export const CHECKOUT_SLOT_TYPE = 'slot'

export const CHECKOUT_SLOT = {
  // Root Cause + Operable selects (rendered near the top of the form).
  ROOT_CAUSE_SELECTS: 'root_cause_selects',
  // The conditional abuse/defect notes + photos revealed by the Root Cause selection.
  ROOT_CAUSE_DETAILS: 'root_cause_details',
}

export const ROOT_CAUSE_OPTIONS = [
  'Manufacturer Defect',
  'Impact or a collision',
  'Neglect or lack of maintenance',
  'No Problem Found',
  'Normal wear',
  'Operator Error',
  'Tampered with, and or is missing components',
]

export const ROOT_CAUSE_NEGLECT_OPTIONS = [
  'Impact or a collision',
  'Neglect or lack of maintenance',
  'Tampered with, and or is missing components',
]

export const ROOT_CAUSE_DEFECT = 'Manufacturer Defect'

export const TRIP_STATUS = {
  CLOSED_NO_CHECKOUT: 'Closed No Checkout',
  EXPIRED: 'Expired',
}

// Statuses a tech can set on a trip at checkout. Intentionally separate from
// ORDER_STATUS so the two vocabularies can drift independently even though
// the string values currently overlap.
export const WORK_STATUS = {
  AWAITING_PARTS: 'Awaiting Parts',
  ESTIMATE: 'Estimate',
  WORK_COMPLETED: 'Work Completed',
}

export const WORK_STATUS_OPTIONS = [
  WORK_STATUS.AWAITING_PARTS,
  WORK_STATUS.ESTIMATE,
  WORK_STATUS.WORK_COMPLETED,
]

export const GEO_BROWSER_OPTIONS = { CHROME: 'Google Chrome', SAFARI: 'Safari' }

export const GEO_DEVICE_OPTIONS = {
  IPHONE: 'iPhone',
  IPAD: 'iPad',
  ANDROID: 'Android',
  WINDOWS: 'Windows Computer',
  MAC: 'Mac Computer',
}

export const AMAZON_GEO_DOCS_URL =
  'https://geolocation-docs.s3.us-east-2.amazonaws.com'

export const QUESTIONNAIRE_TYPE = {
  BLANK: 'blank',
  CUSTOM: 'custom',
  SERIAL_NUMBER: 'serial_number',
  PRE_WORK_MACHINE_STATE: 'pre_work_machine_state',
}

export const GEOLOCATION_STATUS = {
  INSIDE: 'inside',
  OUTSIDE: 'outside',
}

export const LOGIN_TYPE = {
  TECHNICIAN: 1,
  MANAGER: 2,
}
